import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { useMutation, useQuery } from "react-query";
import { useAppContext } from "../../context/appContext";
import idApi from "../../service/IdApi";
import PageHeading from "../../components/PageHeading";
import { throttle } from "lodash";
import LazyState from "../../utils/LazyState";
import PageLoadError from "../../components/PageLoadError";
import { useLocation, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Menu } from "primereact/menu";
import { Dialog } from "primereact/dialog";
import { Column } from "primereact/column";
import axios from "axios";
import providerApi from "../../service/ProviderApi";
import DataUtils from "../../utils/DataUtils";

const MyReviews = () => {
    const { displayAlert } = useAppContext();
    const location = useLocation();
    const [reviews, setReviews] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [firstTimeUser, setFirstTimeUser] = useState(false);
    const [firstTimeChecking, setFirstTimeChecking] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const dataTable = useRef(null);
    const [selectedReviews, setSelectedReviews] = useState([]);
    const [selectedReview, setSelectedReview] = useState(null);
    const [deleteReviewDialog, setDeleteReviewDialog] = useState(false);
    const [isDeleteReviewProcessing, setIsDeleteReviewProcessing] = useState(false);
    const menu = useRef(null);
    const [rowActions, setRowActions] = useState([]);
    const navigate = useNavigate();

    const [lazyState, setLazyState] = useState(() => {
        if (location.state && location.state.lazyState) {
            return location.state.lazyState;
        }
        return LazyState.getMyReviewsLazyState();
    });

    const { isLoading, isError, refetch } = useQuery(["Reviews", lazyState], async () => {
        var isFiltering = false;
        var body = {};
        body.first = lazyState.first;
        if (lazyState.first !== 0) {
            isFiltering = true;
        }
        body.rows = lazyState.rows;
        body.sortField = lazyState.sortField;
        body.sortOrder = lazyState.sortOrder;
        const result = await idApi.post("/auth/user/reviews", body);
        if (firstTimeChecking) {
            if (!isFiltering) {
                // We'll assume they are new until they've given at least 10 reviews
                setFirstTimeUser(result.data.totalRecords <= 10);
            }
            setFirstTimeChecking(false);
        }
        for (var i = 0; i < result.data.reviews.length; i++) {
            const review = result.data.reviews[i];
            try {
                const searchParameters = {
                    q: review.providerId,
                    query_by: 'providerId',
                    per_page: 1,
                    filter_by: `providerId:="${review.providerId}"`,
                    prefix: false,
                    typo_tolerance: 0,
                    match_threshold: 100,
                    infix: 'off'
                };

                const searchResults = await axios({
                    method: 'get',
                    url: `https://${process.env.REACT_APP_TYPESENSE_HOST}/collections/${process.env.REACT_APP_TYPESENSE_COMPANIES_COLLECTION}/documents/search`,
                    params: searchParameters,
                    headers: {
                        'X-TYPESENSE-API-KEY': process.env.REACT_APP_TYPESENSE_API_KEY,
                        'Content-Type': 'application/json'
                    }
                });

                let companyName = "[Company No Longer Exists]";
                let vanityNameLC = "";
                if (searchResults.data?.hits?.length > 0) {
                    if (searchResults.data.hits[0].document?.name) {
                        companyName = searchResults.data.hits[0].document.name;
                    }
                    if (searchResults.data.hits[0].document?.vanityName) {
                        vanityNameLC = searchResults.data.hits[0].document.vanityName;
                    }
                }
                review.companyName = companyName;
                review.vanityNameLC = vanityNameLC.toLowerCase();
            } catch (error) {
                console.error('Typesense search error:', error);
            }
        }
        setReviews(result.data.reviews);
        setTotalRecords(result.data.totalRecords);
        setLoaded(true);
    });

    const deleteReviewMutation = useMutation(() => {
        try {
            return providerApi.delete("provider/profile/reviews/" + selectedReview.vanityNameLC + "/" + selectedReview.reviewNumber);
        } catch (error) {
            throw error;
        }
    });

    const confirmDeleteReview = () => {
        setDeleteReviewDialog(true);
        setIsDeleteReviewProcessing(false);
    };

    const hideDeleteReviewDialog = () => {
        setDeleteReviewDialog(false);
    };

    const deleteReview = throttle(() => {
        setIsDeleteReviewProcessing(true);
        deleteReviewMutation.mutate(selectedReview.reviewNumber, {
            onError: (error, variables, context) => {
                displayAlert("error", "Unexpected Error", "Please try again later");
                setIsDeleteReviewProcessing(false);
            },
            onSuccess: () => {
                displayAlert("success", "Success", "Review deleted successfully");
                refetch();
                setDeleteReviewDialog(false);
                setSelectedReview(null);
            },
        });
    }, 500, { trailing: false });

    useEffect(() => {
        var viewCompanyMenu =
        {
            label: "View Company",
            icon: "pi pi-eye",
            command: () => {
                window.location.href = process.env.REACT_APP_NETWORK_SITE + "/company/" + selectedReview.vanityNameLC;
            },
        }
        var deleteMenu =
        {
            label: "Delete Review",
            icon: "pi pi-trash",
            command: () => {
                confirmDeleteReview();
            },
        }
        var _rowActions = [];
        if (DataUtils.isNotEmpty(selectedReview?.vanityNameLC)) {
            _rowActions.push(deleteMenu);
            _rowActions.push(viewCompanyMenu);
        }
        setRowActions(_rowActions);
    }, [selectedReview]);

    if (isError) {
        return (
            <div>
                <PageLoadError />
            </div>
        );
    }

    const pageHeader = (
        <PageHeading pageTitle="My Reviews">
            <div className="block">
            </div>
        </PageHeading>
    );

    const onPage = (event) => {
        setLazyState(event);
        setSelectedReviews(null);
    };

    const onSort = (event) => {
        setLazyState(event);
        setSelectedReviews(null);
    };

    const showActionMenu = (e, rowData) => {
        setSelectedReview(rowData);
        menu.current.toggle(e);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button
                    icon="pi pi-bars"
                    className="p-button-rounded p-button-icon-only p-button-text text-800"
                    onClick={(e) => showActionMenu(e, rowData)}
                    aria-controls="popup_menu"
                    aria-haspopup
                    tooltip="Request Actions"
                    tooltipOptions={{ showDelay: 1000 }}
                />
            </div>
        );
    };

    const deleteReviewDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteReviewDialog} />
            <Button label="Yes" icon="pi pi-check" onClick={deleteReview} loading={isDeleteReviewProcessing} />
        </>
    );

    const reviewBodyTemplate = (rowData) => {
        return (
            <div className="flex flex-column">
                {rowData.headline && (
                    <div className="">
                        {rowData.headline}
                    </div>
                )}
                <div>
                    {rowData.review}
                </div>
            </div>
        );
    };

    const visibilityBodyTemplate = (rowData) => {
        return (
            <span>{rowData.visibility === "p" ? "Public" : "Friends Only"}</span>
        );
    };
    return (
        <div>
            {pageHeader}
            <div className="layout-content">
                <div className="card">
                    {firstTimeUser && (
                        <div className="mb-3">
                            <div className="text-xl font-medium mb-2">
                                Manage Your Reviews on Wallist
                            </div>
                            <div className="mb-2">
                                Your reviews are more than just ratings, they're valuable recommendations that help your friends and family make informed decisions. By managing and sharing your experiences, you create a trusted network of authentic recommendations that others can rely on.
                            </div>
                            <div className="mb-2">
                                When leaving a review, you can choose who sees it. Public reviews display your full feedback along with your first name and last initial. If you mark your review as friends only, only your star rating will be visible to the public as the full review will only be seen by your friends and the business.
                            </div>
                            <div className="mb-2">
                                Your reviews influence search results by moving your recommendations to the top of the list for <a href="#" onClick={(e) => { e.preventDefault(); navigate("/account/my-friends"); }}>your friends</a> helping them find the right service professionals.
                            </div>
                            <hr />
                        </div>
                    )}
                    <Menu model={rowActions} popup ref={menu} />
                    <DataTable
                        ref={dataTable}
                        value={reviews}
                        selection={selectedReviews}
                        onSelectionChange={(e) => setSelectedReviews(e.value)}
                        dataKey="reviewNumber"
                        paginator
                        rows={lazyState.rows}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                        emptyMessage="No reviews found"
                        lazy
                        first={lazyState.first}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        onSort={onSort}
                        sortField={lazyState.sortField}
                        sortOrder={lazyState.sortOrder}
                        loading={isLoading || !loaded}
                        size="small"
                    >
                        <Column body={actionBodyTemplate} headerStyle={{ width: "1%", minWidth: "1rem" }} />
                        <Column field="companyName" header="Company" />
                        <Column field="visibility" header="Visibility" body={visibilityBodyTemplate} />
                        <Column field="rating" header="Star Rating" />
                        <Column field="review" header="Review" body={reviewBodyTemplate} />
                        <Column field="createdAt" header="Created On" />
                    </DataTable>
                    <Dialog
                        visible={deleteReviewDialog}
                        style={{ width: "450px" }}
                        header="Confirm"
                        modal
                        footer={deleteReviewDialogFooter}
                        onHide={hideDeleteReviewDialog}
                    >
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
                            {selectedReview && (
                                <span>
                                    Are you sure you want to delete the review for <b>{selectedReview.companyName}</b>
                                </span>
                            )}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default MyReviews;
